import React, { useEffect } from "react";
import Card from "components/Card";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  SortingState,
  useReactTable,
  ColumnDef,
  PaginationState
} from "@tanstack/react-table";

interface TablePropsType<TableDataType> {
  tableData: TableDataType[];
  columns: ColumnDef<TableDataType>[];
  tableHeading: string;
  children?: React.ReactNode;
  onRowClick?: (row: TableDataType) => void;
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onPageSizeChange?: (size: number) => void;
}

const Table = <TableDataType,>(props: TablePropsType<TableDataType>) => {
  const { 
    tableData, 
    columns, 
    tableHeading, 
    children, 
    onRowClick,
    totalCount,
    currentPage,
    pageSize,
    hasNextPage,
    hasPreviousPage,
    onNextPage,
    onPreviousPage,
    onPageSizeChange
  } = props;
  
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...tableData]);
  
  // Pagination state for client-side pagination
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Check if using API pagination
  const useApiPagination = totalCount !== undefined;

  useEffect(() => {
    setData(tableData);
  }, [tableData]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination: useApiPagination ? undefined : pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: useApiPagination ? undefined : setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: useApiPagination ? undefined : getPaginationRowModel(),
    manualPagination: useApiPagination,
    debugTable: true,
  });

  return (
    <Card extra={"w-full pb-7 p-4 h-full"}>
      <header className="relative flex items-top justify-between h-20">
        <div className="text-xl font-bold text-navy-700 dark:text-white w-full">
          {tableHeading}
        </div>
        <div className="w-full">
          {children}
        </div>
      </header>
      <div className="overflow-x-auto px-1">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 text-start"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {{
                        asc: "",
                        desc: "",
                      }[header.column.getIsSorted() as string] ?? null}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? ( 
              table
                .getRowModel()
                .rows
                .map((row) => {
                  return (
                    <tr 
                      key={row.id}
                      onClick={() => onRowClick?.(row.original)}
                      className={onRowClick ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors" : ""}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <td
                            key={cell.id}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center pt-8 text-sm font-semibold text-gray-400 dark:text-white min-w-[150px] border-white/0 py-3 pr-4">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="flex justify-between items-center mb-2 mt-5">
          <div className="flex items-center">
            <button
              onClick={() => {
                if (useApiPagination) {
                  onPreviousPage?.();
                } else {
                  table.previousPage();
                }
              }}
              disabled={useApiPagination ? !hasPreviousPage : !table.getCanPreviousPage()}
              className={`px-1 py-2 mr-2 rounded-md ${
                (useApiPagination ? !hasPreviousPage : !table.getCanPreviousPage()) 
                  ? 'dark:bg-gray-800 text-gray-500' 
                  : ' text-brand-500 dark:bg-brand-400 '
              }`}
            >
              <FaChevronLeft />
            </button>
            <button
              onClick={() => {
                if (useApiPagination) {
                  onNextPage?.();
                } else {
                  table.nextPage();
                }
              }}
              disabled={useApiPagination ? !hasNextPage : !table.getCanNextPage()}
              className={`px-1 py-2 rounded-md ${
                (useApiPagination ? !hasNextPage : !table.getCanNextPage()) 
                  ? 'text-gray-500' 
                  : ' text-brand-500 dark:bg-brand-400 '
              }`}
            >
              <FaChevronRight />
            </button>
            <span className="text-base text-gray-600 ml-3">
              {useApiPagination 
                ? `Page ${currentPage}` 
                : `Page ${table.getState().pagination.pageIndex + 1} of ${table.getPageCount()}`
              }
            </span>
          </div>
          <div className="text-center">
            <span className="text-base text-gray-600">
              {useApiPagination 
                ? `Showing ${tableData.length === 0 ? 0 : ((currentPage! - 1) * pageSize! + 1)} to ${Math.min(currentPage! * pageSize!, totalCount)} of ${totalCount} results`
                : `Showing ${table.getRowModel().rows.length} of ${table.getCoreRowModel().rows.length} results`
              }
            </span>
          </div>
          <div>
            <select
              value={useApiPagination ? pageSize : table.getState().pagination.pageSize}
              onChange={e => {
                const newSize = Number(e.target.value);
                if (useApiPagination) {
                  onPageSizeChange?.(newSize);
                } else {
                  table.setPageSize(newSize);
                }
              }}
              className="bg-white rounded-md !border-none !outline-none !ring-0 text-gray-600 disabled:bg-gray-100 disabled:text-gray-400"
            >
              {[5, 10, 20, 30, 40, 50].map(size => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default Table;